// Conversations section inside the Vault (owner-only). Lists every visitor
// conversation grouped by person, and opens a read-only transcript modal.
// The data already lives in the conversation/message tables — this is the
// reference view: "what did each person actually talk to the twin about".

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { useTwinName } from '../lib/twin.js';

interface ConversationRow {
  id: string;
  userId: string | null;
  personName: string;
  personTier: string;
  language: string;
  startedAt: number;
  messageCount: number;
  preview: string;
}

interface TranscriptMessage { id: string; role: string; content: string; createdAt: number }

const TIER_COLOR: Record<string, string> = {
  private: 'text-amber border-amber/40',
  spouse: 'text-rose-300 border-rose-300/30',
  family: 'text-emerald-300 border-emerald-300/30',
  close_friends: 'text-sky-300 border-sky-300/30',
  colleagues: 'text-yellow-200 border-yellow-200/30',
  public: 'text-ink/60 border-ink/20',
};

function fmtDate(sec: number): string {
  try { return new Date(sec * 1000).toLocaleString(); } catch { return ''; }
}

export function ConversationsView() {
  const qc = useQueryClient();
  const twinName = useTwinName();
  const [open, setOpen] = useState<ConversationRow | null>(null);
  const [purgeMsg, setPurgeMsg] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<{ conversations: ConversationRow[] }>({
    queryKey: ['conversation', 'admin', 'all'],
    queryFn: () => api('/api/conversation/admin/all'),
  });

  const purge = useMutation({
    mutationFn: () => api<{ deleted: number }>('/api/conversation/admin/purge-empty', { method: 'POST' }),
    onSuccess: (r) => {
      setPurgeMsg(`${r.deleted} conversation${r.deleted > 1 ? 's' : ''} vide${r.deleted > 1 ? 's' : ''} supprimée${r.deleted > 1 ? 's' : ''}.`);
      qc.invalidateQueries({ queryKey: ['conversation'] });
    },
  });

  // Group conversations by person (name + tier).
  const groups = (() => {
    const m = new Map<string, { name: string; tier: string; rows: ConversationRow[] }>();
    for (const c of data?.conversations ?? []) {
      const key = `${c.userId ?? c.personName}`;
      if (!m.has(key)) m.set(key, { name: c.personName, tier: c.personTier, rows: [] });
      m.get(key)!.rows.push(c);
    }
    return [...m.values()].sort((a, b) => (b.rows[0]?.startedAt ?? 0) - (a.rows[0]?.startedAt ?? 0));
  })();

  if (isLoading) return <div className="text-ink/40 text-[12px] py-6">Loading…</div>;
  if (isError) return <div className="text-red-300 text-[12px] py-6">Couldn’t load conversations.</div>;
  if (groups.length === 0) return <div className="text-ink/40 font-serif italic py-6">No conversations yet. They’ll appear here as people talk with {twinName}.</div>;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-end gap-3 -mb-2">
        {purgeMsg && <span className="text-[11px] text-emerald-300/70">{purgeMsg}</span>}
        <button onClick={() => { setPurgeMsg(null); purge.mutate(); }} disabled={purge.isPending}
          className="px-3 py-1.5 rounded-full border border-ink/15 text-[11px] text-ink/50 hover:text-ink hover:border-ink/30 disabled:opacity-40">
          {purge.isPending ? '…' : 'Purger les vides'}
        </button>
      </div>
      {groups.map((g) => (
        <div key={g.name}>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-serif text-[15px] text-ink">{g.name}</span>
            <span className={`px-1.5 py-0.5 rounded-full border text-[10px] ${TIER_COLOR[g.tier] ?? 'text-ink/50 border-ink/20'}`}>
              {g.tier.replace('_', ' ')}
            </span>
            <span className="text-[11px] text-ink/35">{g.rows.length} conversation{g.rows.length > 1 ? 's' : ''}</span>
          </div>
          <ul className="flex flex-col gap-1">
            {g.rows.map((c) => (
              <li key={c.id}>
                <button onClick={() => setOpen(c)}
                  className="w-full text-left flex items-center gap-3 py-2.5 px-3 rounded-lg border border-ink/[0.06] hover:border-ink/20 hover:bg-white/[0.02] transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-[13.5px] text-ink/80 truncate">{c.preview || '—'}</div>
                    <div className="text-[11px] text-ink/35 mt-0.5">
                      {fmtDate(c.startedAt)} · {c.messageCount} msg · {c.language}
                    </div>
                  </div>
                  <span className="text-[11px] text-ink/40 flex-shrink-0">open →</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {open && <TranscriptModal convo={open} twinName={twinName} onClose={() => setOpen(null)} />}
    </div>
  );
}

function TranscriptModal({ convo, twinName, onClose }: { convo: ConversationRow; twinName: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<{ messages: TranscriptMessage[] }>({
    queryKey: ['conversation', convo.id],
    queryFn: () => api(`/api/conversation/${convo.id}`),
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-ink/15 bg-void" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink/10">
          <div>
            <div className="font-serif text-[16px] text-ink">{convo.personName}</div>
            <div className="text-[11px] text-ink/40">{fmtDate(convo.startedAt)} · {convo.messageCount} messages</div>
          </div>
          <button onClick={onClose} className="text-[11.5px] text-ink/50 hover:text-ink px-2 py-1">close ✕</button>
        </div>
        <div className="overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {isLoading ? <div className="text-ink/40 text-[12px] py-6">Loading…</div>
            : (data?.messages ?? []).filter((m) => m.role !== 'system').map((m) => {
              const isVisitor = m.role === 'visitor';
              return (
                <div key={m.id} className={`flex flex-col ${isVisitor ? 'items-end' : 'items-start'}`}>
                  <span className="text-[10px] text-ink/30 mb-0.5">
                    {isVisitor ? convo.personName : twinName}
                  </span>
                  <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl font-serif text-[14px] leading-relaxed whitespace-pre-wrap ${
                    isVisitor ? 'bg-ink/[0.06] text-ink rounded-br-sm' : 'bg-amber/[0.07] text-ink rounded-bl-sm border border-amber/15'
                  }`}>
                    {m.content}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
